<template>
  <div class="row mb-4 border-top pt-4">
    <div class="col-md-6">
      <label :for="companySelectId" class="form-label fw-semibold"
        >Select Company</label
      >
      <select
        :id="companySelectId"
        v-model="selectedCompanyId"
        class="form-select"
        :disabled="isLoadingCompanies"
      >
        <option value="">-- Select a company --</option>
        <option
          v-for="company in companies"
          :key="company.id"
          :value="company.id"
        >
          {{ company.name }}
        </option>
      </select>
      <div
        v-if="isLoadingCompanies"
        class="spinner-border spinner-border-sm text-primary mt-2"
        role="status"
      >
        <span class="visually-hidden">Loading companies...</span>
      </div>
    </div>

    <div class="col-md-6">
      <label :for="invoiceSelectId" class="form-label fw-semibold"
        >Select Fee Invoice</label
      >
      <select
        :id="invoiceSelectId"
        v-model="invoiceId"
        class="form-select"
        :disabled="isLoadingInvoices"
      >
        <option value="">-- Select an invoice --</option>
        <option
          v-for="invoice in invoices"
          :key="invoice.id"
          :value="invoice.id"
        >
          {{ invoice.invoice_number }} -
          {{
            invoice.requested_at
              ? formatTransactionDate(invoice.requested_at)
              : "No date"
          }}
          - €{{ formatMoneyString(invoice.total)
          }}{{ invoice.is_canceled ? " (canceled)" : "" }}
        </option>
      </select>
      <div
        v-if="isLoadingInvoices"
        class="spinner-border spinner-border-sm text-primary mt-2"
        role="status"
      >
        <span class="visually-hidden">Loading fee invoices...</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatTransactionDate } from "@/admin/emailTemplateDates";
import { formatMoneyString } from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { apiErrorMessage } from "@/utils/apiErrors";
import { onBeforeUnmount, onMounted, ref, useId, watch } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

interface FeeInvoiceCompany {
  id: number;
  name: string;
}

interface FeeInvoice {
  id: number;
  invoice_number: string;
  requested_at: string | null;
  /** Decimal string, e.g. "124.00" */
  total: string;
  is_canceled: boolean;
}

const props = defineProps<{
  language: string;
}>();

const emit = defineEmits<{
  /** The notification email rendered for the selected invoice in `language`. */
  preview: [htmlContent: string];
  /** A user-facing message for a preview that could not be rendered. */
  error: [message: string];
}>();

/** The selected fee invoice; cleared when the company changes or the picker goes away. */
const invoiceId = defineModel<number | "">({ default: "" });

const { globalErrorHandler } = useGlobalHandlers();

const companySelectId = useId();
const invoiceSelectId = useId();

const companies = ref<FeeInvoiceCompany[]>([]);
const invoices = ref<FeeInvoice[]>([]);
const selectedCompanyId = ref<number | "">("");
const isLoadingCompanies = ref(false);
const isLoadingInvoices = ref(false);

onMounted(() => {
  fetchCompanies();
});

onBeforeUnmount(() => {
  invoiceId.value = "";
});

watch(selectedCompanyId, () => {
  invoiceId.value = "";
  fetchInvoices();
});

watch([invoiceId, () => props.language], () => {
  fetchPreview();
});

const fetchCompanies = async () => {
  try {
    isLoadingCompanies.value = true;
    const response = await goSolidApi.get<{ companies: FeeInvoiceCompany[] }>(
      "/api/admin/emails/fee-invoice-companies"
    );
    companies.value = response.data.companies;
  } catch (err: unknown) {
    globalErrorHandler(err);
  } finally {
    isLoadingCompanies.value = false;
  }
};

const fetchInvoices = async () => {
  if (!selectedCompanyId.value) {
    invoices.value = [];
    return;
  }

  try {
    isLoadingInvoices.value = true;
    const response = await goSolidApi.get<{ invoices: FeeInvoice[] }>(
      `/api/admin/emails/fee-invoices?company_id=${selectedCompanyId.value}`
    );
    invoices.value = response.data.invoices;
  } catch (err: unknown) {
    globalErrorHandler(err);
  } finally {
    isLoadingInvoices.value = false;
  }
};

const fetchPreview = async () => {
  if (!invoiceId.value) {
    return;
  }

  try {
    const response = await goSolidApi.post<{ html_content: string }>(
      "/api/admin/emails/templates/fee-invoice-notification/preview",
      {
        invoice_id: invoiceId.value,
        language: props.language,
      }
    );
    emit("preview", response.data.html_content);
  } catch (err: unknown) {
    emit(
      "error",
      apiErrorMessage(err, "Failed to preview fee invoice notification")
    );
    globalErrorHandler(err);
  }
};
</script>
